import { useEffect, useState, type ReactNode } from 'react';
import type { ProjectRefDto } from '../../shared/api-types.js';
import type { ApiFailure, ApiResult } from '../api.js';
import { Button, Dialog, EM_DASH, FormErrorSummary, inlineFailureText, ProjectChip } from '../ui/index.js';
import { ProjectCheckboxes, type ProjectOption } from './project-checkboxes.js';

// Filing by hand, wherever a record carries projects (design §7.4; owner ruling 3 of 2026-10-02: a to-do, a meeting,
// a finding or a matter may sit in several). One small dialog — the ProjectCheckboxes composition behind an Apply —
// used by the to-do row's "File under…", the meeting record's "Change projects" and the Edit filing control on a
// finding's and a matter's card; and one cluster that draws a record's chips, or the muted dash for none. The
// caller sends the set and decides the toast: a filing is undoable (03-components §4.2 prefers undo to confirm),
// and the words of that toast belong to the surface that asked.

/** How a record's projects are shown on every surface: a wrapping cluster of label chips, or the dash for none. */
export function ProjectChipList({ projects, link = true, emptyLabel = 'No project', className }: {
  projects: readonly ProjectRefDto[];
  /** False inside a label, heading or caption, where a nested link would be wrong. */
  link?: boolean;
  /** The accessible name of the dash drawn for a record in no project (04-data §4: never a blank). */
  emptyLabel?: string;
  className?: string;
}) {
  // Why `role="img"`: ARIA forbids a name on a bare span and the desktop screen readers ignore one there — the dash was
  // heard and its reason never; a named graphic is announced as its label (04-data §4: never a blank, and said).
  if (projects.length === 0) return <span className="muted" role="img" aria-label={emptyLabel}>{EM_DASH}</span>;
  return (
    <span className={['cluster', 'project-chips', className].filter(Boolean).join(' ')}>
      {projects.map((project) => <ProjectChip key={project.id} project={project} link={link} />)}
    </span>
  );
}

export interface ProjectFilingDialogProps<T> {
  open: boolean;
  /** The record being filed, named: "File “Send the proposal” under…". */
  title: ReactNode;
  description?: ReactNode;
  /** The project ids the record holds now; the dialog opens with them ticked. */
  initial: readonly string[];
  /** The reader's own live projects, when the caller already holds them; omitted, the checkboxes load them. */
  options?: readonly ProjectOption[] | null;
  onClose: () => void;
  /** Sends the whole set; the answer is handed to `onApplied` with what changed. */
  onApply: (projectIds: string[]) => Promise<ApiResult<T>>;
  onApplied: (data: T, change: { previous: string[]; next: string[] }) => void;
  confirmLabel?: string;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = new Set(a);
  return b.every((id) => left.has(id));
}

/**
 * The one filing dialog (03-components §4.1): the checkboxes, Apply disabled until the set differs from what the record
 * holds, the server's refusal shown in place, and the dialog closed by the caller once applied. Size sm: a list of
 * chips and one text field (design §7.4). Why no toast here: the caller knows the record's words and what Undo sends.
 */
export function ProjectFilingDialog<T>({ open, title, description, initial, options, onClose, onApply, onApplied, confirmLabel = 'Apply' }: ProjectFilingDialogProps<T>) {
  const [selected, setSelected] = useState<string[]>([...initial]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Why on open and not on `initial` alone: a parent re-rendering with an equal set must not wipe what the person has
  // ticked since the dialog opened.
  useEffect(() => {
    if (open) {
      setSelected([...initial]);
      setError(null);
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dirty = !sameSet(selected, initial);

  const apply = async (): Promise<void> => {
    if (saving || !dirty) return;
    setSaving(true);
    setError(null);
    const result = await onApply(selected);
    setSaving(false);
    if (!result.ok) {
      setError(failureSentence(result));
      return;
    }
    onApplied(result.data, { previous: [...initial], next: selected });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      initialFocus="first"
      unsavedChanges={dirty && !saving}
      actions={
        <>
          {/* Why only Apply carries a reason (app-baseline 05-client §5.5; Phase 1c D16): it waits for a change to the
              ticks, which the reader must be told by keyboard as by pointer; Cancel is held only while the apply is in
              flight, which Apply's loading state already says (AB-Q7). */}
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => void apply()} loading={saving} disabled={!dirty} disabledReason={dirty ? undefined : FILING_UNCHANGED_REASON}>{confirmLabel}</Button>
        </>
      }
    >
      <div className="stack stack--3">
        <FormErrorSummary errors={error ? [error] : []} />
        <ProjectCheckboxes options={options} selected={selected} onChange={setSelected} disabled={saving} />
      </div>
    </Dialog>
  );
}

/** Why Apply waits: the ticks still match what the record holds (03-components §2; app-baseline 05-client §5.5). */
export const FILING_UNCHANGED_REASON = 'No changes to apply.';

/** The server's sentence with its reference, for the form's summary. */
export function failureSentence(failure: ApiFailure): string {
  return failure.correlationId ? `${inlineFailureText(failure)} (reference ${failure.correlationId})` : inlineFailureText(failure);
}

/** The names a toast says after a filing, in the order the server lists them (by name). */
export function projectNames(projects: readonly Pick<ProjectRefDto, 'name'>[]): string[] {
  return projects.map((project) => project.name);
}
